import { Button, Tooltip } from '@mantine/core';
import { IconBrain } from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';

import { useAssistant } from './api';
import { AssistantDrawer } from './AssistantDrawer';
import { AssistantContext, type AssistantPatient, useAssistantControls } from './context';

/** Fournit l'assistant à toute l'application (bouton de la barre du haut, boutons
 * « Assistant IA » des pages) ; rien n'apparaît sans le module ou sans IA configurée. */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const { ready } = useAssistant();
  const [opened, setOpened] = useState(false);
  const [patient, setPatient] = useState<AssistantPatient | null>(null);
  const controls = useMemo(
    () => ({
      open: (p?: AssistantPatient | null) => {
        if (p !== undefined) setPatient(p);
        setOpened(true);
      },
    }),
    [],
  );
  return (
    <AssistantContext.Provider value={controls}>
      {children}
      {ready && (
        <AssistantDrawer
          // Un autre dossier joint ouvre une nouvelle discussion.
          key={patient?.id ?? 'aucun'}
          opened={opened}
          onClose={() => setOpened(false)}
          patient={patient}
          onDetachPatient={() => setPatient(null)}
        />
      )}
    </AssistantContext.Provider>
  );
}

export function AssistantTopButton() {
  const { ready } = useAssistant();
  const { open } = useAssistantControls();
  if (!ready) return null;
  return (
    <Tooltip label="Assistant intelligent : questions, rédaction, résumés">
      <Button
        className="ai-btn"
        variant="light"
        color="violet"
        leftSection={<IconBrain size={16} />}
        onClick={() => open()}
      >
        GestClinic AI
      </Button>
    </Tooltip>
  );
}

/** Bouton « Assistant IA » d'un dossier : ouvre l'assistant avec ce dossier joint. */
export function AskAboutPatientButton({ patientId, label }: { patientId: string; label: string }) {
  const { ready } = useAssistant();
  const { open } = useAssistantControls();
  if (!ready) return null;
  return (
    <Button
      size="sm"
      variant="subtle"
      color="violet"
      leftSection={<IconBrain size={16} />}
      onClick={() => open({ id: patientId, label })}
    >
      Assistant IA
    </Button>
  );
}
